import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, TableSortLabel } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import { visuallyHidden } from '@mui/utils';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from 'react-virtuoso';
import { colors, fontSize, fontWeight, fontFamilyPrimary } from '../../theme';
import { OperatorsEnum } from '../../utils/enum/OperatorsType';
import { SurveyStatusToLabel } from '../Surveys/surveyInterface';
import DateFilter from './DateFilter';
import FilterDropDown from './FilterDropdown';
import { TableCellBodyStyled, TableCellHeaderStyled, TableRowBodyStyled, TableRowHeaderStyled, TextFieldDivStyled, TextFieldStyled, TableContainerStyled, TableCellStyled } from './styled';
import './styles.css';
import { VirtualizedTableProps, CreateRowCellInterface, Header, HEADER_TYPE, FilterDateInterface } from './type';

interface EnhancedTableHeadProps {
    headCells: Header[];
    order: 'asc' | 'desc';
    orderBy: string;
    onRequestSort: (event: React.MouseEvent<unknown>, property: string, type: HEADER_TYPE) => void;
    onRequestFilter: (property: string, filterText: string[]) => void;
    onRequestFilterDate: (property: string, filterDate?: FilterDateInterface) => void;
    lastFocusedItem: string;
    hasDeleteAction?: boolean;
    hasReloadAction?: boolean;
    hasUpdateAction?: boolean;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
    const { headCells, order, orderBy, onRequestSort, onRequestFilter, lastFocusedItem, onRequestFilterDate, hasDeleteAction, hasReloadAction, hasUpdateAction } = props;
    const { t } = useTranslation(['dashboard', 'hotAlerts']);

    const createSortHandler = (property: string, type: HEADER_TYPE) => (event: any) => {
        onRequestSort(event, property, type);
    };

    const createFilterHandler = (property: string, filterText: string[]) => {
        onRequestFilter(property, filterText);
    };

    const createFilterDateHandler = (property: string, filterDate?: FilterDateInterface) => {
        onRequestFilterDate(property, filterDate);
    };

    return (
        <TableRowHeaderStyled>
            {headCells.map((headCell: Header) => (
                <TableCellHeaderStyled
                    key={headCell.id}
                    align={headCell.type === HEADER_TYPE.NUMERIC ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    style={{
                        maxWidth: '500px',
                        width: 'auto',
                        position: 'sticky',
                        right: 'auto',
                        backgroundColor: colors.grey800,
                        zIndex: 1,
                        borderLeftStyle: 'none',
                        borderWidth: 'none',
                        borderColor: 'none',
                        fontFamily: fontFamilyPrimary,
                        fontWeight: fontWeight.semiBold,
                        fontSize: fontSize.m,
                        padding: '9px 0',
                        borderBottom: 'none',
                        verticalAlign: 'text-top',
                    }}
                >
                    <TableCellStyled paddingcell='5px'>
                        <TableSortLabel
                            active={orderBy === headCell.rowKey}
                            direction={orderBy === headCell.rowKey ? order : 'asc'}
                            onClick={createSortHandler(headCell.rowKey, headCell.type)}
                            title={headCell.label}
                        >
                            {headCell.label}
                            {orderBy === headCell.rowKey ? (
                                <Box component='span' sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCellStyled>
                    <TextFieldDivStyled>
                        {headCell.displayFilter && headCell.type === HEADER_TYPE.CHOICE && (
                            <FilterDropDown
                                columnId={headCell.rowKey}
                                filterValues={headCell.values}
                                handleSelectedFilterValues={createFilterHandler}
                                selectedFilterValues={headCell.filter}
                            />
                        )}
                        {headCell.displayFilter && headCell.type === HEADER_TYPE.OPEN && (
                            <TextFieldStyled
                                autoFocus={headCell.id === lastFocusedItem}
                                id={`filter-${headCell.id}`}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    createFilterHandler(headCell.rowKey, [event.target.value]);
                                }}
                                placeholder={t('dashboard:addIndicatorModal.selectDisplayType.indicatorChart.filterContent')}
                                size="small"
                                type="search"
                                value={headCell.filter || ''}
                                variant="standard"
                                hiddenLabel
                            />
                        )}
                        {headCell.type === HEADER_TYPE.DATE && headCell.filterDate && (
                            <DateFilter
                                columnId={headCell.id}
                                handleSelectedFilterDateValue={createFilterDateHandler}
                                selectedFilterDateValue={headCell.filterDate}
                            />
                        )}
                    </TextFieldDivStyled>
                </TableCellHeaderStyled>
            ))}
            {hasUpdateAction && (
                <TableCellHeaderStyled
                    style={{
                        backgroundColor: colors.grey800,
                        borderBottom: 'none',
                        padding: '9px 0',
                    }}
                />
            )}
            {hasReloadAction && (
                <TableCellHeaderStyled
                    style={{
                        backgroundColor: colors.grey800,
                        borderBottom: 'none',
                        padding: '9px 0',
                    }}
                />
            )}
            {hasDeleteAction && (
                <TableCellHeaderStyled
                    style={{
                        backgroundColor: colors.grey800,
                        borderBottom: 'none',
                        padding: '9px 0',
                    }}
                />
            )}
        </TableRowHeaderStyled>
    );
}

function compare(a: any, b: any, property: any) {
    const aEntryValue = a[property];
    const bEntryValue = b[property];
    const aValue = aEntryValue ? String(aEntryValue).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
    const bValue = bEntryValue ? String(bEntryValue).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
    if (aValue < bValue) {
        return -1;
    }
    if (aValue > bValue) {
        return 1;
    }

    return 0;
}

const VirtualizedTable: FC<VirtualizedTableProps> = ({ rows, headers, handleDeleteRow, handleReloadRow, handleUpdateRow }) => {
    const [headCells, setHeadCells] = useState<Array<any>>([]);
    const [data, setData] = useState<Array<any>>([]);
    const [filteredData, setFilteredData] = useState<Array<any>>([]);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('');
    const [filters, setFilters] = useState<{[key: string]: string[]}>({});
    const [filterDate, setFilterDate] = useState<{[key: string]: FilterDateInterface}>({});
    const [lastFocusedItem, setLastFocusedItem] = useState('');

    useEffect(() => {
        const dynamicCells = headers.map((column, index) => (
            {
                ...column,
                rank: index,
            }
        ));
        dynamicCells.sort((a, b) => a.rank - b.rank);
        setHeadCells(dynamicCells);

        // Créer les données
        const rowsData = rows.map((row: any, index: number) => {
            const rowData: any = {
                id: index,
            };

            headers.forEach((column) => {
                const columnKey = column.rowKey;
                if (column.id === 'surveyStatus') {
                    rowData[columnKey] = SurveyStatusToLabel[row[columnKey] as keyof typeof SurveyStatusToLabel];
                } else {
                    rowData[columnKey] = row[columnKey];
                }
            });

            return rowData;
        });
        setData(rowsData);
        setFilteredData(rowsData);
    }, [rows]);

    const handleRequestSort = (event: any, property: any, type: HEADER_TYPE) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        const sortedData = [...filteredData].sort((a, b) => {
            if (type === HEADER_TYPE.DATE) {
                // Handle date sorting
                const dateA = new Date(a[property]);
                const dateB = new Date(b[property]);

                if (newOrder === 'asc') {
                    return dateA.getTime() - dateB.getTime();
                }

                return dateB.getTime() - dateA.getTime();
            }

            return newOrder === 'asc' ? compare(a, b, property) : -compare(a, b, property);
        });

        setFilteredData(sortedData);
        setOrder(newOrder);
        setOrderBy(property);
    };

    const applyDateFilter = (row: any, key: string, filterValues: FilterDateInterface): boolean => {
        if (!filterValues.date || !filterValues.operator) {
            return true; // No filter applied, include the row
        }

        const rowDate = new Date(row[key]);
        const tmpFilterDate = filterValues.date;
        const rowDateOnly = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const tmpFilterDateOnly = new Date(tmpFilterDate.getFullYear(), tmpFilterDate.getMonth(), tmpFilterDate.getDate());

        switch (filterValues.operator) {
            case OperatorsEnum.EQUAL:
                return rowDate.toDateString() === tmpFilterDate.toDateString();

            case OperatorsEnum.SUPERIOR:
                return rowDateOnly > tmpFilterDateOnly;

            case OperatorsEnum.SUPERIOR_EQUAL:
                return rowDateOnly >= tmpFilterDateOnly;

            case OperatorsEnum.INFERIOR:
                return rowDateOnly < tmpFilterDateOnly;

            case OperatorsEnum.INFERIOR_EQUAL:
                return rowDateOnly <= tmpFilterDateOnly;

            default:
                return true; // Unknown operator, include the row
        }
    };

    // Extract the general filtering logic
    const applyGeneralFilters = (dataset: any[], applyFilters: any, applyHeadCells: any[]) => {
        let filtered = [...dataset];
        Object.keys(applyFilters).forEach((key) => {
            const filterValues = applyFilters[key];
            const currentHeadCell = applyHeadCells.find((hc) => hc.rowKey === key);

            if (currentHeadCell && filterValues.length > 0) {
                if (currentHeadCell.type === HEADER_TYPE.CHOICE) {
                    filtered = filtered.filter((row) => (
                        filterValues.findIndex((val: any) => val === row[key]) >= 0
                    ));
                }
                if (currentHeadCell.type === HEADER_TYPE.OPEN) {
                    filtered = filtered.filter((row) => String(row[key] || '').toLowerCase().includes(filterValues[0].toLowerCase()));
                }
            }
        });

        return filtered;
    };

    // Extract the date filtering logic
    const applyDateFilters = (dataset: any[], applyFilterDate: any, applyHeadCells: any[]) => {
        let filtered = [...dataset];

        Object.keys(applyFilterDate).forEach((key) => {
            const filterValues = applyFilterDate[key];
            const currentHeadCell = applyHeadCells.find((hc) => hc.id === key);

            if (currentHeadCell && filterValues) {
                filtered = filtered.filter((row) => applyDateFilter(row, key, filterValues));
            }
        });

        return filtered;
    };

    const handleFilter = (property: string, filterText: string[]) => {
        const newFilters = { ...filters };
        newFilters[property] = filterText;
        setLastFocusedItem(property);
        setFilters(newFilters);
        setHeadCells((prev) => {
            const headCellsCopy = [...prev];
            const indexOf = headCellsCopy.findIndex((hc) => hc.id === property);

            if (indexOf !== -1) {
                headCellsCopy[indexOf].filter = filterText;
            }

            return headCellsCopy;
        });

        // Apply general filters first
        let filtered = applyGeneralFilters(data, newFilters, headCells);
        // Then apply date filters
        filtered = applyDateFilters(filtered, filterDate, headCells);
        setFilteredData(filtered);
    };

    const handleFilterDate = (property: string, entryFilterDate?: FilterDateInterface) => {
        if (!entryFilterDate) {
            return;
        }
        const newFilterDate = { ...filterDate };
        newFilterDate[property] = entryFilterDate;
        setLastFocusedItem(property);
        setFilterDate(newFilterDate);
        setHeadCells((prev) => {
            const headCellsCopy = [...prev];
            const indexOf = headCellsCopy.findIndex((hc) => hc.id === property);

            if (indexOf !== -1) {
                headCellsCopy[indexOf].filterDate = entryFilterDate;
            }

            return headCellsCopy;
        });

        // Apply general filters first
        let filtered = applyGeneralFilters(data, filters, headCells);
        // Then apply date filters
        filtered = applyDateFilters(filtered, newFilterDate, headCells);

        setFilteredData(filtered);
    };

    const scrollbarStyles = {
        '&::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: colors.grey600,
            borderRadius: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: colors.grey400,
            borderRadius: '6px',
            '&:hover': {
                background: '#555',
            },
        },
    };

    const virtuosoComponents = useMemo(() => {
        const Scroller = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof TableContainer>>((props, ref) => (
            <TableContainer
                {...props}
                ref={ref}
                style={{
                    // eslint-disable-next-line
                    ...props.style,
                    overflow: 'auto',
                }}
                sx={{ ...scrollbarStyles }}
            />
        ));
        Scroller.displayName = 'VirtuosoScroller';

        const VirtuosoTable = (props: React.ComponentProps<typeof Table>) => (
            <Table
                stickyHeader
                {...props}
                style={{
                    borderCollapse: 'separate',
                    tableLayout: 'auto', // Add for better performance
                    width: 'auto',
                }}
            />
        );

        const VirtuosoTableHead = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
            <TableHead {...props} ref={ref} />
        ));
        VirtuosoTableHead.displayName = 'VirtuosoTableHead';

        const VirtuosoTableBody = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
            <TableBody {...props} ref={ref} />
        ));
        VirtuosoTableBody.displayName = 'VirtuosoTableBody';

        // TableRow that uses data-index for zebra background
        const VirtuosoTableRow = (props: any) => (
            <TableRowBodyStyled
                // eslint-disable-next-line
                bgcolor='transparent'
                {...props}
                hover={false}
            />
        );

        return {
            Scroller,
            Table: VirtuosoTable,
            TableHead: VirtuosoTableHead,
            TableRow: VirtuosoTableRow,
            TableBody: VirtuosoTableBody,
        };
        // Depend only on stable values; scrollbarStyles is stable (local const) and colors/imports are stable
    }, []);

    const CreateRowCell = ({
        currentColumn, row,
    }: CreateRowCellInterface) => {
        if (currentColumn.type === HEADER_TYPE.DATE && row) {
            const dateToDisplay = new Date(row).toLocaleString();

            return (
                <span title={dateToDisplay}>
                    {dateToDisplay}
                </span>
            );
        }
        if (row) {
            return (
                <span title={row}>
                    {row}
                </span>
            );
        }

        // Add explicit default return
        return null;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <TableContainerStyled>
                <TableVirtuoso
                    components={virtuosoComponents}
                    data={filteredData}
                    fixedHeaderContent={() => (
                        <EnhancedTableHead
                            hasDeleteAction={handleDeleteRow !== undefined}
                            hasReloadAction={handleReloadRow !== undefined}
                            hasUpdateAction={handleUpdateRow !== undefined}
                            headCells={headCells}
                            lastFocusedItem={lastFocusedItem}
                            onRequestFilter={handleFilter}
                            onRequestFilterDate={handleFilterDate}
                            onRequestSort={handleRequestSort}
                            order={order}
                            orderBy={orderBy}
                        />
                    )}
                    increaseViewportBy={200}
                    itemContent={(index, row) => (
                        <>
                            {headCells.map((headCell) => (
                                <TableCellBodyStyled
                                    key={headCell.id}
                                    // eslint-disable-next-line
                                    bgcolor={'transparent'}
                                    // eslint-disable-next-line
                                    style={{
                                        maxWidth: '500px',
                                        width: 'auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        position: 'relative',
                                        right: 'auto',
                                        zIndex: 1,
                                        borderLeftStyle: 'none',
                                        borderLeftWidth: 'none',
                                        borderColor: 'none',
                                    }}
                                >
                                    <CreateRowCell
                                        currentColumn={headCell}
                                        row={row[headCell.rowKey] || ''}
                                    />
                                </TableCellBodyStyled>
                            ))}
                            {handleUpdateRow !== undefined && (
                                <TableCellBodyStyled
                                    // eslint-disable-next-line
                                    bgcolor={'transparent'}
                                    style={{
                                        padding: '0 4px',
                                        borderLeftStyle: 'none',
                                        borderLeftWidth: 'none',
                                        borderColor: 'none',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleUpdateRow(row)}
                                        size="small"
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </TableCellBodyStyled>
                            )}
                            {handleReloadRow !== undefined && (
                                <TableCellBodyStyled
                                    // eslint-disable-next-line
                                    bgcolor={'transparent'}
                                    style={{
                                        padding: '0 4px',
                                        borderLeftStyle: 'none',
                                        borderLeftWidth: 'none',
                                        borderColor: 'none',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleReloadRow(row)}
                                        size="small"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </TableCellBodyStyled>
                            )}
                            {handleDeleteRow !== undefined && (
                                <TableCellBodyStyled
                                    // eslint-disable-next-line
                                    bgcolor={'transparent'}
                                    style={{
                                        padding: '0 4px',
                                        borderLeftStyle: 'none',
                                        borderLeftWidth: 'none',
                                        borderColor: 'none',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleDeleteRow(row)}
                                        size="small"
                                    >
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </TableCellBodyStyled>
                            )}
                        </>
                    )}
                    overscan={5}
                    style={{
                        height: '100%',
                    }}
                />
            </TableContainerStyled>
        </div>
    );
};

export default VirtualizedTable;
